/**
 * MessageRow — single chat bubble for ConnectionChat. Handles text + image +
 * audio + file rendering plus the system welcome banner.
 */
import { Download, FileText } from "lucide-react";

function attachmentUrl(attId) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/connect/attachment/${attId}`;
}

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function MessageRow({ m }) {
  if (m.kind === "system") {
    return (
      <div className="text-center text-[11px] text-muted-foreground" data-testid="chat-system-message">
        <span className="inline-block rounded-full bg-muted px-3 py-1">{m.body}</span>
      </div>
    );
  }
  const isMine = m.is_mine;
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[78%] sm:max-w-[64%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          isMine
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        }`}
        data-testid={`chat-msg-${m.message_id}`}
      >
        {m.kind === "image" && m.attachment_id && (
          <a href={attachmentUrl(m.attachment_id)} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={attachmentUrl(m.attachment_id)}
              alt={m.attachment_name || "image"}
              loading="lazy"
              className="rounded-lg max-h-72 w-auto bg-black/10"
            />
          </a>
        )}
        {m.kind === "audio" && m.attachment_id && (
          <audio controls preload="metadata" className="max-w-full" src={attachmentUrl(m.attachment_id)} />
        )}
        {m.kind === "file" && m.attachment_id && (
          <a
            href={attachmentUrl(m.attachment_id)}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 rounded-lg p-2 ${isMine ? "bg-white/15" : "bg-card border border-border"}`}
            data-testid="chat-file-link"
          >
            <FileText className="h-4 w-4 shrink-0" />
            <span className="text-xs flex-1 truncate">{m.attachment_name || "file"}</span>
            <Download className="h-3.5 w-3.5 shrink-0" />
          </a>
        )}
        {m.body && (
          <p className={`whitespace-pre-wrap break-words ${m.kind !== "text" ? "mt-2" : ""}`}>{m.body}</p>
        )}
        <p className={`mt-1 text-[9px] ${isMine ? "text-primary-foreground/70" : "text-muted-foreground"} text-right`}>
          {timeShort(m.sent_at)}
        </p>
      </div>
    </div>
  );
}
